<template>
  <form
    autocomplete="off"
    class="gap-0 gap-y-4 "
    @submit.prevent="submitForm"
  >
    <div class="col-span-12 max-w-md mx-auto text-center">
      <template v-if="authStore.user.isVerified">
        <h2 class="">
          Verification Complete
        </h2>

        <span class="mb-8 block">
          Your account is now verified with <strong>{{ authStore.user?.phoneNumber }}</strong>!
        </span>

        <AppButton
          class="btn-outline btn-secondary w-full"
          @click="close"
        >
          Ok
        </AppButton>
      </template>

      <template v-else>
        <div class="col-span-12 max-w-md mx-auto text-center">
          <h2 class="">
            Account Verification
          </h2>

          <span class="text-secondary text-base -mt-4 mb-7 block">
            Step 2: Enter The OTP
          </span>

          <span class="mb-8 block">
            We have sent an OTP to your cellphone number. Please enter the code below to verify your account.
          </span>

          <TextInput
            v-model="formData.code"
            label="OTP Code"
            required
            class="mb-8"
            :validation-error="errors"
            autofocus
          />

          <AppButton
            :processing="isSubmitting || cooldown !== 0"
            class="btn btn-solid py-2.5 w-full mb-6"
            :class="{'btn-gray': (cooldown > 0), 'btn-secondary': (cooldown < 1), }"
          >
            {{ cooldown === 0 ? 'Verify Account' : `Retry in ${cooldown}s` }}
          </AppButton>

          <span class="block">
            Didn’t receive the code?
          </span>

          <AppButton
            class="border-0 text-sm font-normal -mt-5 p-0"
            type="button"
            @click="resendCode"
          >
            <span class="text-secondary text-sm font-medium underline underline-offset-2 block">
              Click here to resend.
            </span>
          </AppButton>
        </div>

        <template v-if="error">
          <span class="text-red-500 p-2 bg-red-100 rounded block mt-3">
            {{ error }}
          </span>
        </template>

        <template v-if="emailResent">
          <span
            class="text-green-700 border border-green-700 bg-[#0F7E6738] rounded flex items-center justify-center gap-2 p-2 mt-3"
          >
            A new verification code has been sent to your cellphone number and the old one is no longer valid.
          </span>
        </template>
      </template>
    </div>
  </form>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { HttpStatusCode } from 'axios';

import { AppButton } from '@/components/app';
import { TextInput } from '@/components/forms/inputs';

import { useFormSubmit, useHttp } from '@/composables';
import { useAuthStore } from '@/store/index';

const authStore = useAuthStore();

const { isSubmitting, errors, error } = useFormSubmit();

const emit = defineEmits(['close']);

const props = defineProps({
  phoneNumber: String,
});

const cooldown = ref(0);

const formData = reactive({
  phoneNumber: props.phoneNumber,
  code: null,
});

const submitForm = async () => {
  errors.value = null;
  error.value = null;
  emailResent.value = false;
  isSubmitting.value = true;

  const response = await useHttp().post('/profile/verification/token', formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    authStore.user = response.data;

    emit('close');
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.exception;
  } else if (response.statusCode === HttpStatusCode.TooManyRequests) {
    error.value = response.exception;

    cooldown.value = error.value.replace(/.*?(\d+).*/, '$1');
    error.value = error.value.split('.')[0];

    while (cooldown.value > 0) {
      await new Promise(resolve => {
        setTimeout(() => {
          cooldown.value -= 1;

          resolve();
        }, 1000);
      });
    }
  } else {
    errors.value = response.exception;
  }

  isSubmitting.value = false;
};

const emailResent = ref(false);

const resendCode = async () => {
  errors.value = null;
  error.value = null;
  emailResent.value = false;

  const response = await useHttp().post('/profile/verification', { phoneNumber: authStore.user.phoneNumber });

  if (response.statusCode === HttpStatusCode.Ok) {
    emailResent.value = true;
  } else if (response.statusCode === HttpStatusCode.TooManyRequests) {
    error.value = response.exception;
  } else {
    errors.value = response.exception;
  }

  isSubmitting.value = false;
};

const close = () => {
  if (!isSubmitting.value) {
    emit('close');
  }
};

</script>
